export function sortLoungePeople(people, onlineUsers, filter = 'all', limit = 24) {
  const safePeople = Array.isArray(people) ? people : [];
  const safeOnline = onlineUsers instanceof Set ? onlineUsers : new Set(onlineUsers || []);

  return safePeople
    .filter((person) => person && person.username)
    .filter((person) => (filter === 'online' ? safeOnline.has(person.username) : true))
    .sort((left, right) => Number(safeOnline.has(right.username)) - Number(safeOnline.has(left.username)))
    .slice(0, limit);
}

export function countOnlinePeople(people, onlineUsers) {
  const safePeople = Array.isArray(people) ? people : [];
  const safeOnline = onlineUsers instanceof Set ? onlineUsers : new Set(onlineUsers || []);

  return safePeople.filter((person) => person?.username && safeOnline.has(person.username)).length;
}

export function totalUnreadCount(conversations) {
  return (Array.isArray(conversations) ? conversations : []).reduce(
    (sum, conversation) => sum + Number(conversation?.unread_count || 0),
    0
  );
}

export function unreadConversations(conversations) {
  return (Array.isArray(conversations) ? conversations : []).filter((conversation) => Number(conversation?.unread_count || 0) > 0);
}

export function sortConversationsByLatest(conversations) {
  return [...(Array.isArray(conversations) ? conversations : [])].sort((left, right) => {
    const leftTime = Date.parse(left?.latest_message_at || left?.created_at || 0) || 0;
    const rightTime = Date.parse(right?.latest_message_at || right?.created_at || 0) || 0;
    return rightTime - leftTime;
  });
}

export function getTierKind(tier) {
  if (tier === null || tier === undefined) return 'base';
  if (typeof tier === 'number') {
    if (tier >= 3) return 'verified';
    if (tier === 2) return 'premium';
    return 'base';
  }

  const value = String(tier).toLowerCase();
  if (value.includes('verified') || value.includes('signature')) return 'verified';
  if (value.includes('premium')) return 'premium';
  return 'base';
}

export const DEFAULT_LOUNGE_FILTERS = {
  gender: 'all',
  minAge: 18,
  maxAge: 80,
  maxDistanceKm: 50,
  maxRate: 200,
  tiers: ['base', 'premium', 'verified'],
};

export function isDefaultLoungeFilters(filters) {
  return JSON.stringify(filters) === JSON.stringify(DEFAULT_LOUNGE_FILTERS);
}

// Missing profile fields (age/gender not yet set, rate defaulted to "0") never exclude a person;
// filters only apply once a person actually has that attribute populated.
export function applyLoungeFilters(people, filters) {
  const safePeople = Array.isArray(people) ? people : [];
  const activeFilters = { ...DEFAULT_LOUNGE_FILTERS, ...(filters || {}) };

  return safePeople.filter((person) => {
    if (!person) return false;

    if (activeFilters.gender !== 'all' && person.gender && person.gender !== activeFilters.gender) {
      return false;
    }

    const age = Number(person.age);
    if (Number.isFinite(age) && age > 0 && (age < activeFilters.minAge || age > activeFilters.maxAge)) {
      return false;
    }

    const rate = Number(person.rate_per_hour);
    if (Number.isFinite(rate) && rate > activeFilters.maxRate) {
      return false;
    }

    const distance = Number(person.distance_km ?? person.distanceKm);
    if (Number.isFinite(distance) && distance > activeFilters.maxDistanceKm) {
      return false;
    }

    if (!activeFilters.tiers.includes(getTierKind(person.tier_id))) {
      return false;
    }

    return true;
  });
}

// Only writes params that differ from the default, keeping URLs clean when no filters are active.
export function filtersToSearchParams(filters) {
  const params = new URLSearchParams();
  const activeFilters = { ...DEFAULT_LOUNGE_FILTERS, ...(filters || {}) };

  if (activeFilters.gender !== DEFAULT_LOUNGE_FILTERS.gender) {
    params.set('gender', activeFilters.gender);
  }
  if (activeFilters.minAge !== DEFAULT_LOUNGE_FILTERS.minAge) {
    params.set('minAge', String(activeFilters.minAge));
  }
  if (activeFilters.maxAge !== DEFAULT_LOUNGE_FILTERS.maxAge) {
    params.set('maxAge', String(activeFilters.maxAge));
  }
  if (activeFilters.maxDistanceKm !== DEFAULT_LOUNGE_FILTERS.maxDistanceKm) {
    params.set('maxDistanceKm', String(activeFilters.maxDistanceKm));
  }
  if (activeFilters.maxRate !== DEFAULT_LOUNGE_FILTERS.maxRate) {
    params.set('maxRate', String(activeFilters.maxRate));
  }

  const sortedTiers = [...activeFilters.tiers].sort();
  const sortedDefaultTiers = [...DEFAULT_LOUNGE_FILTERS.tiers].sort();
  if (JSON.stringify(sortedTiers) !== JSON.stringify(sortedDefaultTiers)) {
    params.set('tiers', activeFilters.tiers.join(','));
  }

  return params;
}

export function filtersFromSearchParams(searchParams) {
  const params = searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams || '');

  const minAge = Number(params.get('minAge'));
  const maxAge = Number(params.get('maxAge'));
  const maxDistanceKm = Number(params.get('maxDistanceKm'));
  const maxRate = Number(params.get('maxRate'));
  const tiers = params.get('tiers');

  return {
    gender: params.get('gender') || DEFAULT_LOUNGE_FILTERS.gender,
    minAge: params.has('minAge') && Number.isFinite(minAge) ? minAge : DEFAULT_LOUNGE_FILTERS.minAge,
    maxAge: params.has('maxAge') && Number.isFinite(maxAge) ? maxAge : DEFAULT_LOUNGE_FILTERS.maxAge,
    maxDistanceKm: params.has('maxDistanceKm') && Number.isFinite(maxDistanceKm) ? maxDistanceKm : DEFAULT_LOUNGE_FILTERS.maxDistanceKm,
    maxRate: params.has('maxRate') && Number.isFinite(maxRate) ? maxRate : DEFAULT_LOUNGE_FILTERS.maxRate,
    tiers: tiers ? tiers.split(',').filter(Boolean) : [...DEFAULT_LOUNGE_FILTERS.tiers],
  };
}
